import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import { getListUserSourcedArtifactQueryKey } from '@/api/artifact.hook'
import { getListControlsQueryKey } from '@/api/control.hook'
import {
  forceAnalyzeControl,
  listSubcategories,
  reanalyzeAssessmentControls,
  runPromptOnCompany,
  updateArtifactSubcategory,
} from '@/gen/artifacts/analyzer/v1/analyzer_service-AnalyzerService_connectquery'
import { listCompanyArtifact } from '@/gen/inventory/v1/artifact_service-ArtifactService_connectquery'

import { defaultTransport } from '@/lib/connect/connect-transport'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

export const getListCompanyArtifactQueryKey = () => {
  return createConnectQueryKey({
    schema: listCompanyArtifact,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useListSubcategories = () => {
  return useQuery(listSubcategories)
}

export const useUpdateArtifactSubcategory = () => {
  const queryClient = useQueryClient()
  return useMutation(updateArtifactSubcategory, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
    },
  })
}

export const useRunPromptOnCompany = () => {
  return useMutation(runPromptOnCompany)
}

export const useForceAnalyzeControl = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(forceAnalyzeControl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      toast({
        title: 'Control analysis started successfully',
        description: 'This control is being analyzed.',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Failed to start control analysis',
        description: 'An error occurred while starting the analysis.',
        variant: 'destructive',
      })
    },
  })
}

export const useReanalyzeAssessmentControls = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(reanalyzeAssessmentControls, {
    onSuccess: ({ controlsQueued }) => {
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      toast({
        title: 'Control analysis started',
        description: `${controlsQueued} ${controlsQueued === 1 ? 'control is' : 'controls are'} being analyzed.`,
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        title: 'Failed to start control analysis',
        description: 'An error occurred while starting the analysis.',
        variant: 'destructive',
      })
    },
  })
}
